/**
 * Multi-brand (multi-domain) reference data.
 *
 * The same app is served on several domains; each domain maps to a brand.
 * A brand defines the site identity (name, domain, emails), the text
 * replacements applied to page contents, and the <meta name="theme-color">.
 *
 * Theme colors themselves live in CSS: src/styles/input.css defines the
 * default (dammi) palette on :root and per-brand overrides on
 * :root[data-brand="..."], which src/utils/brand.ts activates at runtime.
 * Tailwind's `indigo`/`purple` scales are remapped to those variables in
 * tailwind.config.ts, so existing utility classes switch automatically.
 */

export type BrandId = "dammi" | "propti";

export interface BrandReplacement {
  pattern: RegExp;
  replacement: string;
}

export interface BrandConfig {
  id: BrandId;
  /** Hostnames (including their subdomains) served under this brand. */
  hostnames: string[];
  /** Display name of the site, e.g. used as og:site_name. */
  siteName: string;
  /** Primary domain, also used to build brand email addresses. */
  domain: string;
  /** Value applied to <meta name="theme-color">. */
  themeColor: string;
  /**
   * Ordered text replacements applied to page contents (title, meta tags,
   * visible text, mailto/site links). Empty for the default brand, whose
   * references are the ones written in the source pages.
   */
  replacements: BrandReplacement[];
  /**
   * Dedicated favicon / app-icon set. When set, it replaces the favicon
   * links written in page-head (which point at the default brand's logo).
   */
  icons?: BrandIcons;
}

export interface BrandIcons {
  ico: string;
  svg: string;
  appleTouch: string;
  manifest: string;
}

export const DEFAULT_BRAND_ID: BrandId = "dammi";

export const BRANDS: Record<BrandId, BrandConfig> = {
  dammi: {
    id: "dammi",
    hostnames: ["dammi.lv"],
    siteName: "DAMMI.LV",
    domain: "dammi.lv",
    themeColor: "#667eea",
    replacements: [],
  },
  propti: {
    id: "propti",
    hostnames: ["propti.eu"],
    siteName: "PROPTI.EU",
    domain: "propti.eu",
    themeColor: "#3b82f6",
    icons: {
      ico: "assets/propti/favicon.ico",
      svg: "assets/propti/favicon.svg",
      appleTouch: "assets/propti/apple-touch-icon.png",
      manifest: "assets/propti/site.webmanifest",
    },
    replacements: [
      { pattern: /DAMMI\.LV/g, replacement: "PROPTI.EU" },
      // Negative lookbehind keeps subdomain references (brescia.dammi.lv,
      // latvija.dammi.lv, ...) pointing at the original projects.
      { pattern: /(?<![\w.-])dammi\.lv/gi, replacement: "propti.eu" },
      { pattern: /\bDAMMI\b/g, replacement: "PROPTI" },
      // Logo assets (src/assets/propti-logo.{svg,png})
      { pattern: /dammi-logo/g, replacement: "propti-logo" },
    ],
  },
};
